/**
 * buildWizardRailItems — the rail's live one-line summaries (W12 Part B, Task B3).
 *
 * A pure function of what the request currently carries: every caption is a
 * count the SUBMIT handler would serialise, never a picker internal («what you
 * see is what is sent», FP-7).
 *
 * Status rule (tightened by the designer REVIEW, Task B9d — the rail is the one
 * element an operator trusts without reading, so it may never claim more than
 * the request carries, FP-7):
 * - `error` — the step's selection blocks the request: Tasks (`no_task_routes`)
 *   and Project variables (`empty_variable_package`) in the feasibility
 *   contract, and Building blocks when a materialized example contradicts the
 *   block-type whitelist (SPEC-019, m46 — the submit refuses it), and Insights
 *   when the thesis selection starves a displayed scope (W13 IS2, AC-W13-04 —
 *   the server refuses it) or the thesis universe could not be read at all
 *   (I3). An error never turns into `done` by being visited.
 *   `analysis_not_pinned` is deliberately NOT an error: nothing is pinned when
 *   the wizard opens, and a rail that starts red would be noise, not a signal
 *   (the footer already names the missing pin).
 * - `done`  — a REQUIRED step (`analysis`, `tasks`, `projectVars`) only once its
 *   input is actually satisfied — visiting it never completes it; an OPTIONAL
 *   step when it carries a selection or the operator has visited it (its
 *   defaults are already correct, so a visited step is a confirmed one);
 *   `review` only when the readiness verdict is not blocked.
 * - `pending` — everything before that, and ALWAYS the step the operator is
 *   standing on (the approved mock renders the active step as a blue numeral,
 *   never a ✓ — `WizardRail` drops `completed` on the active step).
 */

import { WIZARD_STEPS, type WizardRailItem, type WizardStepKey, type WizardStepStatus } from './wizardSteps';

/**
 * W13 IS2, ruling W13-R27 — what an UNNARROWED thesis selection sends: nothing.
 * The request omits `insightIds`, so the server keeps its own shortlist and the
 * surfaces say so instead of a count that never travels. One string, shared by
 * the rail, the quick screen and the review row.
 */
export const INSIGHT_DEFAULT_PROJECTION_CAPTION = 'All theses (default projection)';

/** `…` past `max` characters — rail captions are clipped at ~118px. */
export const ellipsizeRailCaption = (value: string, max: number): string =>
  (value.length > max ? `${value.slice(0, max)}…` : value);

export interface WizardRailInput {
  /** Human name of the pinned analysis (`null` = nothing pinned). */
  pinnedAnalysisName: string | null;
  pinned: boolean;
  /** Chosen insight-variable generation (`''` = product variables only). */
  generationChosen: boolean;
  selectedTaskCount: number;
  availableTaskCount: number;
  selectedInsightVariableCount: number;
  availableInsightVariableCount: number;
  selectedProjectVariableCount: number;
  availableProjectVariableCount: number;
  variablesFollowTasks: boolean;
  selectedSocialProofCount: number;
  /** Zero task routes selected — the feasibility contract's `no_task_routes`. */
  taskRoutesBlocked: boolean;
  /** W13 IS2: theses the request is grounded in, of the ones the step displays. */
  selectedInsightThesisCount: number;
  availableInsightThesisCount: number;
  /**
   * W13 IS2 / ruling W13-R27: the selection actually NARROWS (at least one
   * displayed thesis unchecked). While nothing is unchecked the request carries
   * no `insightIds` and the server renders its own default projection — the rail
   * must say THAT, not a count the request does not carry (FP-7).
   */
  insightSelectionNarrows?: boolean;
  /**
   * W13 IS2 (I3): the thesis universe could not be read. The run would fall back
   * to the default projection, which is a decision the owner must make
   * knowingly — so the step reads `error` and the launch is gated, never silent.
   */
  insightRowsFailed?: boolean;
  /**
   * W13 IS2 (AC-W13-04): the selection left a DISPLAYED scope — the global
   * package or a selected task — with zero theses. The server refuses such a
   * request (`task_without_theses`), so the step that owns it reads `error`.
   */
  insightSelectionStarved?: boolean;
  /** `strategyTypeLabel(formData.strategyType)` — the Details & tone step (Task B4). */
  strategyTypeCaption: string;
  /** Block types the whitelist still allows, of the catalogue's ten (Task B5). */
  allowedBlockTypeCount: number;
  /** Project subtypes the whitelist still allows (Task B5). */
  selectedSubtypeCount: number;
  /** Library examples the request carries (Task B5). */
  libraryExampleCount: number;
  /**
   * SPEC-019 (m46): a materialized library example whose block type the
   * «Block types» whitelist excludes. The request cannot be sent while it
   * holds, so the step that owns it is `error` — never a ✓.
   */
  libraryConflict?: boolean;
  /** Short name of the selected AI model (`resolveModelPricing().shortName`, Task B6). */
  modelShortName: string;
  /** Screens are generated together with the block plan (Task B6). */
  generateScreens: boolean;
  /**
   * The variable package empties the request — the feasibility contract's
   * `empty_variable_package` (Task B6). Marks the step that can fix it.
   */
  variablePackageBlocked: boolean;
  /**
   * The readiness verdict blocks generation (`feasibility.state === 'blocked'`).
   * «Review & create» is never `done` while it does — the rail would otherwise
   * undermine the readiness panel two lines below it (Task B9d must-fix 3).
   */
  readinessBlocked: boolean;
  /**
   * The step the operator is standing on. It is never reported `done`: the
   * approved mock shows the active step as the blue numeral.
   */
  activeStep: WizardStepKey;
  visitedSteps: ReadonlySet<WizardStepKey>;
}

/**
 * Steps whose input is REQUIRED for the request to mean anything. Visiting one
 * says nothing about it — only its selection does.
 */
const REQUIRED_STEPS: ReadonlySet<WizardStepKey> = new Set<WizardStepKey>([
  'analysis',
  'tasks',
  'projectVars',
]);

export function buildWizardRailItems(input: WizardRailInput): WizardRailItem[] {
  const captionOf = (key: WizardStepKey): string => {
    switch (key) {
      case 'analysis':
        return input.pinnedAnalysisName ? ellipsizeRailCaption(input.pinnedAnalysisName, 18) : '—';
      case 'tasks':
        return input.pinned
          ? `${input.selectedTaskCount} of ${input.availableTaskCount} routes`
          : '—';
      case 'insights':
        // Nothing is pinned ⇒ nothing to ground on yet; the step says so too.
        if (!input.pinned) return '—';
        if (input.insightRowsFailed) return 'could not be read';
        // Ruling W13-R27: «what you see is what is SENT». Unnarrowed means the
        // server's own top-N projection travels, not this count.
        if (!input.insightSelectionNarrows) return INSIGHT_DEFAULT_PROJECTION_CAPTION;
        return `${input.selectedInsightThesisCount} of ${input.availableInsightThesisCount} `
          + `thes${input.availableInsightThesisCount === 1 ? 'is' : 'es'}`;
      case 'insightVars':
        return input.generationChosen
          ? `${input.selectedInsightVariableCount} of ${input.availableInsightVariableCount}`
          : '—';
      case 'projectVars':
        return input.variablesFollowTasks
          ? `${input.selectedProjectVariableCount} · follow tasks`
          : `${input.selectedProjectVariableCount} of ${input.availableProjectVariableCount}`;
      case 'proof':
        return `${input.selectedSocialProofCount}`;
      case 'details':
        return input.strategyTypeCaption || '—';
      case 'blockTypes':
        return `${input.allowedBlockTypeCount} · ${input.selectedSubtypeCount} `
          + `subtype${input.selectedSubtypeCount === 1 ? '' : 's'}`;
      case 'library':
        return input.libraryExampleCount === 0
          ? 'none'
          : `${input.libraryExampleCount} example${input.libraryExampleCount === 1 ? '' : 's'}`;
      case 'generation':
        // What the run will DO, in the order the operator decided it: which
        // model, and whether screens come with the plan (the review switch).
        return `${input.modelShortName ? ellipsizeRailCaption(input.modelShortName, 18) : '—'}`
          + ` · ${input.generateScreens ? 'with screens' : 'plan only'}`;
      case 'review':
        return '';
      default:
        return '—';
    }
  };

  const statusOf = (key: WizardStepKey): WizardStepStatus => {
    if (key === 'tasks' && input.taskRoutesBlocked) return 'error';
    if (key === 'projectVars' && input.variablePackageBlocked) return 'error';
    if (key === 'library' && input.libraryConflict) return 'error';
    if (key === 'insights' && (input.insightSelectionStarved || input.insightRowsFailed)) return 'error';
    // The step in hand is the blue numeral, whatever it carries.
    if (key === input.activeStep) return 'pending';
    const selected = (): boolean => {
      switch (key) {
        case 'analysis': return input.pinned;
        case 'tasks': return input.selectedTaskCount > 0;
        // Optional: the step opens with every thesis checked, so a selection
        // (or a visit, per the rule below) is what completes it.
        case 'insights': return input.selectedInsightThesisCount > 0;
        case 'insightVars': return input.selectedInsightVariableCount > 0;
        case 'projectVars': return input.selectedProjectVariableCount > 0;
        case 'proof': return input.selectedSocialProofCount > 0;
        // The block-type whitelist starts complete, so only a VISIT confirms it
        // (same rule as Details & tone); the library starts empty.
        case 'library': return input.libraryExampleCount > 0;
        default: return false;
      }
    };
    // The terminal step reports the VERDICT, not the visit.
    if (key === 'review') {
      return !input.readinessBlocked && input.visitedSteps.has('review') ? 'done' : 'pending';
    }
    if (REQUIRED_STEPS.has(key)) return selected() ? 'done' : 'pending';
    return selected() || input.visitedSteps.has(key) ? 'done' : 'pending';
  };

  return WIZARD_STEPS.map((step) => ({
    key: step.key,
    label: step.label,
    caption: captionOf(step.key),
    status: statusOf(step.key),
  }));
}
